// Reactive view model over a room: everything the UI shows is derived here
// from the server snapshot plus the ticking clock, using the shared maths.

import { getContext, setContext } from 'svelte';
import {
  catchUpPlan,
  checkInSummary,
  computeDelta,
  currentIndex,
  eventPace,
  lineTitle,
  marathonPhase,
  marathonStats,
  project,
  projectedEnd,
  runTiming,
  scheduleStatus,
  upcomingIndexes,
} from '../../shared/derive.ts';
import type { MutatingAction } from '../../shared/protocol.ts';
import type { CheckIn, LogKind, NoteKind, RoomState, RunKey, RunNote } from '../../shared/types.ts';
import { clock } from './clock.svelte.ts';
import type { RoomConnection } from './room.svelte.ts';
import { toasts } from './toasts.svelte.ts';
import { ui } from './ui.svelte.ts';

export type Tone = 'on' | 'ahead' | 'behind' | 'idle';

/** A room with nothing in it yet; also the defaults for an older offline snapshot. */
export const EMPTY_STATE: RoomState = {
  rev: 0,
  currentKey: null,
  finishedAt: null,
  runs: {},
  log: [],
  logSeq: 0,
  message: null,
  announcement: null,
  twitchChannel: '',
  drift: { enabled: false, intervalMin: 5, thresholdSec: 10 },
  capture: null,
  captureBusy: false,
  tracking: { twitch: false, vision: false, autoApply: false, nodecg: true },
  detection: null,
  settled: null,
  stream: null,
  nodecg: null,
  undo: null,
  updatedAt: 0,
};

/** Builds the view model. A class expression closes over `room`, so the
 * `$derived` fields can use it directly. */
function createLive(room: RoomConnection) {
  return new (class {
    readonly room = room;

    lines = $derived(room.schedule?.lines ?? []);
    state = $derived(room.state ?? EMPTY_STATE);
    now = $derived(clock.now);

    curIndex = $derived(currentIndex(this.lines, this.state));
    current = $derived(this.lines[this.curIndex] ?? null);
    record = $derived(this.current ? this.state.runs[this.current.key] : undefined);
    timing = $derived(this.current ? runTiming(this.record, this.now) : null);
    elapsedSec = $derived(this.timing ? Math.floor(this.timing.elapsedMs / 1000) : 0);

    phase = $derived(marathonPhase(this.lines, this.state, this.now));
    delta = $derived(computeDelta(this.lines, this.state, this.now));
    status = $derived(this.delta == null ? null : scheduleStatus(this.delta));
    tone = $derived<Tone>(this.status ?? 'idle');

    projection = $derived(project(this.lines, this.state, this.now));
    projectedEnd = $derived(projectedEnd(this.projection));
    /** How this event's runs and changeovers have gone (null until a few runs finish). */
    pace = $derived(eventPace(this.lines, this.state));
    /** The end if the rest of the marathon goes at that pace. */
    likelyEnd = $derived(
      this.pace ? projectedEnd(project(this.lines, this.state, this.now, this.pace)) : null,
    );
    /** What could give time back when the end is projected late (null when it isn't). */
    catchUp = $derived(catchUpPlan(this.lines, this.state, this.now, this.pace));
    stats = $derived(marathonStats(this.lines, this.state, this.now));
    upcoming = $derived(upcomingIndexes(this.lines, this.state, 5));
    next = $derived(this.lines[this.upcoming[0] ?? -1] ?? null);
    checkIns = $derived(checkInSummary(this.lines, this.state.runs, this.upcoming));
    /** Operators' private run notes, by run (empty for anyone else). */
    notesByRun = $derived(
      room.notes.reduce<Record<RunKey, RunNote[]>>((by, n) => {
        (by[n.runKey] ??= []).push(n);
        return by;
      }, {}),
    );

    titleOf(key: RunKey | null | undefined): string {
      const line = this.lines.find((l) => l.key === key);
      return line ? lineTitle(line) : 'Unknown run';
    }
  })();
}

export type Live = ReturnType<typeof createLive>;

/** Operator actions, with the confirmation toasts each one deserves. */
export class Ops {
  readonly room: RoomConnection;
  readonly live: Live;

  constructor(room: RoomConnection, live: Live) {
    this.room = room;
    this.live = live;
  }

  private send(action: MutatingAction): boolean {
    return this.room.send(action);
  }

  /**
   * Sends an undoable action and, once the server confirms it changed something,
   * offers an Undo pinned to that exact change (not whatever is newest by then).
   */
  private async withUndo(title: string, action: MutatingAction): Promise<void> {
    const result = await this.room.request(action);
    if (!result?.ok || !result.undo) return;
    const { id } = result.undo;
    toasts.push({ kind: 'info', title, action: { label: 'Undo', run: () => this.undo(id) } });
  }

  toggleTimer(): void {
    if (this.live.phase === 'complete') {
      toasts.push({
        kind: 'info',
        title: 'The marathon is complete',
        body: 'Select a run to reopen it.',
      });
      return;
    }
    this.send({ action: this.live.timing?.phase === 'running' ? 'timer:stop' : 'timer:start' });
  }

  advance(): void {
    this.send({ action: 'run:advance' });
  }

  back(): void {
    this.send({ action: 'run:back' });
  }

  /** Undoes the change the operator is looking at (the latest one by default). */
  undo(id: number | undefined = this.live.state.undo?.id): void {
    this.send({ action: 'undo', id });
  }

  reset(): void {
    void this.withUndo('Timer reset', { action: 'timer:reset' });
  }

  setElapsed(seconds: number): void {
    this.send({ action: 'timer:set', seconds });
  }

  select(key: RunKey): void {
    void this.withUndo(`Jumped to ${this.live.titleOf(key)}`, { action: 'run:select', key });
  }

  skip(key?: RunKey): void {
    const title = this.live.titleOf(key ?? this.live.current?.key);
    void this.withUndo(`Skipped ${title}`, { action: 'run:skip', key });
  }

  unskip(key: RunKey): void {
    this.send({ action: 'run:unskip', key });
  }

  editTimes(key: RunKey, startedAt: number | null, endedAt: number | null): boolean {
    return this.send({ action: 'run:edit', key, startedAt, endedAt });
  }

  checkIn(key: RunKey, status: CheckIn | null): void {
    this.send({ action: 'runner:checkin', key, status });
  }

  refreshSchedule(): void {
    if (this.send({ action: 'schedule:refresh' })) {
      this.room.refreshing = true;
      toasts.push({ kind: 'info', title: 'Re-importing schedule…', timeout: 2500 });
    }
  }

  /** Clears the whole marathon for everyone, after a typed confirmation. */
  async resetMarathon(): Promise<void> {
    const running = this.live.timing?.phase === 'running';
    const live = running
      ? `${this.live.titleOf(this.live.current?.key)} is live right now.\n\n`
      : '';
    const ok = await ui.ask({
      title: 'Reset the whole marathon?',
      body:
        `${live}Clears the live run, every run’s times, skips and check-ins, the event log, private tech notes and issues, the announcement, the message board, the last stream capture and the undo history — for everyone.\n\n` +
        'The schedule, Twitch channel, auto drift and auto-tracking settings stay. This can’t be undone here, but a backup of the current state is kept on the server.',
      confirmLabel: 'Reset marathon',
      danger: true,
      typeToConfirm: 'reset',
    });
    if (!ok) return;
    const result = await this.room.request({ action: 'room:reset' });
    if (result?.ok) {
      toasts.push({
        kind: 'success',
        title: 'Marathon reset',
        body: 'Back to a fresh start. The previous state was backed up on the server.',
      });
    }
  }

  capture(): void {
    this.send({ action: 'capture:run' });
  }

  /** Applies the stream reading the operator is looking at (`id`), not a newer one. */
  applyCapture(id: string): void {
    void this.withUndo('Applied the stream timer', { action: 'capture:apply', id });
  }

  configureDrift(enabled: boolean, intervalMin: number, thresholdSec: number): void {
    this.send({ action: 'drift:configure', enabled, intervalMin, thresholdSec });
  }

  configureTracking(twitch: boolean, vision: boolean, autoApply: boolean, nodecg: boolean): void {
    this.send({ action: 'tracking:configure', twitch, vision, autoApply, nodecg });
  }

  /** Follows the run change the stream showed (the exact detection the operator saw). */
  acceptDetection(id: string): void {
    const d = this.live.state.detection;
    const title = d?.id === id ? this.live.titleOf(d.runKey) : 'the stream';
    const summary =
      d?.id === id && d.kind === 'finish'
        ? `Stopped ${title} when the stream did`
        : `Followed the stream to ${title}`;
    void this.withUndo(summary, { action: 'detection:accept', id });
  }

  dismissDetection(id: string): void {
    this.send({ action: 'detection:dismiss', id });
  }

  setTwitch(channel: string): void {
    this.send({ action: 'twitch:set', channel });
  }

  addLog(text: string, kind: Exclude<LogKind, 'system' | 'warning'>): boolean {
    return this.send({ action: 'log:add', text, kind });
  }

  removeLog(id: number): void {
    this.send({ action: 'log:remove', id });
  }

  clearLog(): void {
    this.send({ action: 'log:clear' });
  }

  setMessage(text: string, color: string): boolean {
    return this.send({ action: 'message:set', text, color });
  }

  clearMessage(): void {
    this.send({ action: 'message:clear' });
  }

  addNote(key: RunKey, kind: NoteKind, text: string): boolean {
    return this.send({ action: 'note:add', key, kind, text });
  }

  editNote(id: number, kind: NoteKind, text: string): boolean {
    return this.send({ action: 'note:edit', id, kind, text });
  }

  /** Deletes a private note, after asking: notes aren't undoable. */
  async removeNote(note: RunNote): Promise<void> {
    const ok = await ui.ask({
      title: note.kind === 'issue' ? 'Delete this issue?' : 'Delete this tech note?',
      body: `“${note.text.length > 140 ? `${note.text.slice(0, 140)}…` : note.text}”\n\nIt goes from the event report too. This can’t be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) this.send({ action: 'note:remove', id: note.id });
  }

  setAnnouncement(text: string, color: string): boolean {
    return this.send({ action: 'announcement:set', text, color });
  }

  clearAnnouncement(): void {
    void this.withUndo('Announcement cleared', { action: 'announcement:clear' });
  }
}

const LIVE = Symbol('live');
const OPS = Symbol('ops');

export function provideLive(room: RoomConnection): { live: Live; ops: Ops } {
  const live = createLive(room);
  const ops = new Ops(room, live);
  setContext(LIVE, live);
  setContext(OPS, ops);
  return { live, ops };
}

export function getLive(): Live {
  return getContext<Live>(LIVE);
}

export function getOps(): Ops {
  return getContext<Ops>(OPS);
}
